import { useState, useEffect } from 'react';
import foxHead from '@/assets/fox-head.png';
import { User, Settings, LogOut, Menu, LayoutDashboard, Target, BookOpen, BarChart3, Heart, Lightbulb, Sparkles, X, ChevronDown, GraduationCap, Sun, Moon } from 'lucide-react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthImage } from '@/hooks/useAuthImage';
import { LanguageToggle } from '@/components/LanguageToggle';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';

// navigation items moved inside component for i18n

export const DashboardHeader = () => {
  const { user, signOut } = useAuth();
  const { subscribed, tier } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [libraryExpanded, setLibraryExpanded] = useState(location.pathname === '/biblioteka');
  const [rawProfileImageUrl, setRawProfileImageUrl] = useState<string | null>(null);
  const profileImageUrl = useAuthImage(rawProfileImageUrl);
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();

  // Library sub-items for mobile
  const librarySubItems = [
    { title: t('library.tabs.generated'), href: '/biblioteka?tab=generated' },
    { title: t('library.tabs.all'), href: '/biblioteka?tab=all' },
    { title: t('library.tabs.best'), href: '/biblioteka?tab=best' },
    { title: t('library.tabs.used'), href: '/biblioteka?tab=used' },
    { title: t('library.tabs.trash'), href: '/biblioteka?tab=trash' },
  ];

  // Fetch user profile image
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (!user?.id) return;

      // Step 1: get instagram_username and potential stored profile_image_url
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('instagram_username, profile_image_url')
        .eq('user_id', user.id)
        .maybeSingle();

      // Prefer stored profile_image_url if present
      if (userProfile?.profile_image_url && userProfile.profile_image_url.trim() !== '') {
        setRawProfileImageUrl(userProfile.profile_image_url);
        return;
      }

      // Step 2: fallback to latest instagram_daily_metrics.profile_pic_url for user's username
      if (userProfile?.instagram_username) {
        const { data: metrics } = await supabase
          .from('instagram_daily_metrics')
          .select('profile_pic_url')
          .ilike('username', userProfile.instagram_username)
          .order('scraped_at', { ascending: false })
          .limit(1);

        const pic = metrics?.[0]?.profile_pic_url;
        if (pic && pic.trim() !== '') {
          setRawProfileImageUrl(pic);
        }
      }
    };

    fetchProfileImage();
  }, [user?.id]);

  const navigationItems = [
    { title: t('nav.dashboard'), href: '/panel', icon: LayoutDashboard },
    { title: t('nav.library'), href: '/biblioteka', icon: BookOpen },
    { title: t('nav.favorites'), href: '/ulubione', icon: Heart },
    { title: t('nav.ideaGenerator'), href: '/kreator-scenariusza', icon: Sparkles },
    { title: t('nav.inspiration'), href: '/inspiracje', icon: Lightbulb },
    { title: t('nav.competitors'), href: '/konkurencja', icon: Target },
    { title: t('nav.analytics'), href: '/analityka', icon: BarChart3 },
    { title: 'Akademia', href: '/akademia', icon: GraduationCap },
    { title: t('nav.settings'), href: '/ustawienia', icon: Settings },
  ];

  return (
    <header className="fixed md:sticky left-0 right-0 z-50 h-16 min-h-16 flex-shrink-0 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ top: 'var(--banner-height, 0px)' }}>
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Left side - Mobile menu + title */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Mobile Menu Drawer */}
          <Drawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} direction="top">
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="top-0 bottom-auto mt-0 rounded-t-none rounded-b-xl max-h-[92vh] flex flex-col">
              <DrawerHeader className="border-b py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <DrawerTitle className="text-left">
                    <div className="flex items-center gap-2">
                      <img src={foxHead} alt="Viraluj mascot" className="w-7 h-7 object-contain flex-shrink-0" />
                      <h2 className="text-xl font-bold leading-none bg-gradient-to-r from-orange-500 to-orange-400 bg-clip-text text-transparent">Viraluj.pl</h2>
                    </div>
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <X className="h-5 w-5" />
                    </Button>
                  </DrawerClose>
                </div>
              </DrawerHeader>
              <ScrollArea className="flex-1 overflow-y-auto">
                <nav className="p-4 space-y-1 pb-10">
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.href || 
                      (item.href === '/biblioteka' && location.pathname === '/biblioteka');
                    const isLibrary = item.href === '/biblioteka';
                    
                    if (isLibrary) {
                      return (
                        <Collapsible 
                          key={item.href} 
                          open={libraryExpanded} 
                          onOpenChange={setLibraryExpanded}
                        >
                          <CollapsibleTrigger asChild>
                            <button
                              className={cn(
                                "w-full flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors",
                                "hover:bg-accent hover:text-accent-foreground",
                                isActive 
                                  ? "bg-primary text-primary-foreground shadow-sm" 
                                  : "text-muted-foreground"
                              )}
                            >
                              <item.icon className="h-5 w-5 mr-3" />
                              <span className="flex-1 text-left">{item.title}</span>
                              <ChevronDown className={cn(
                                "h-4 w-4 transition-transform duration-200",
                                libraryExpanded && "rotate-180"
                              )} />
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-1 mt-1">
                            {librarySubItems.map((subItem) => {
                              const isSubActive = location.pathname + location.search === subItem.href;
                              return (
                                <DrawerClose key={subItem.href} asChild>
                                  <NavLink
                                    to={subItem.href}
                                    className={cn(
                                      "flex items-center pl-12 pr-4 py-2.5 rounded-lg text-sm transition-colors",
                                      "hover:bg-accent hover:text-accent-foreground",
                                      isSubActive 
                                        ? "bg-primary/20 text-primary font-medium" 
                                        : "text-muted-foreground"
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    <span>{subItem.title}</span>
                                  </NavLink>
                                </DrawerClose>
                              );
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    }
                    
                    return (
                      <DrawerClose key={item.href} asChild>
                        <NavLink
                          to={item.href}
                          className={cn(
                            "flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors",
                            "hover:bg-accent hover:text-accent-foreground",
                            isActive 
                              ? "bg-primary text-primary-foreground shadow-sm" 
                              : "text-muted-foreground"
                          )}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <item.icon className="h-5 w-5 mr-3" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DrawerClose>
                    );
                  })}
                </nav>
              </ScrollArea>
            </DrawerContent>
          </Drawer>
          
          <h1 className="text-base md:text-lg font-semibold">
            {navigationItems.find(item => item.href === location.pathname)?.title || t('nav.dashboard')}
          </h1>
        </div>

        {/* Right side - language toggle and user menu */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Language Toggle */}
          <LanguageToggle />

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  {profileImageUrl && (
                    <AvatarImage 
                      src={profileImageUrl} 
                      alt="Profile"
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback>
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex flex-col space-y-1 p-2">
                <p className="text-sm font-medium leading-none">
                  {user?.email || 'User'}
                </p>
                {subscribed && tier && (
                  <span className="inline-flex items-center mt-1 w-fit text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize">
                    Plan {tier === 'pro' ? 'Pro' : 'Starter'}
                  </span>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/ustawienia')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>{t('header.settings')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('header.logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};